"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { MoreHorizontal } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * UN SEUL BOUTON PAR LIGNE : « ••• ». Il ouvre un menu qui ne propose que ce
 * qui est possible ; la suppression est à part, en dernier.
 *
 * Le menu est rendu dans « body » (portail) : dans un tableau qui défile
 * horizontalement, un menu absolu serait rogné par « overflow ».
 */
export interface ActionMenu {
  libelle: string;
  onClick: () => void;
  icone?: ReactNode;
  /** L'action dangereuse : séparée, en rouge, en dernier. */
  danger?: boolean;
  /** Une action absente n'est pas affichée ; c'est plus lisible qu'une action grisée. */
  visible?: boolean;
}

interface Props {
  actions: ActionMenu[];
  titre?: string;
  /** Le bouton qui ouvre le menu ; par défaut le « ••• » discret. */
  declencheur?: ReactNode;
  className?: string;
}

export function MenuActions({ actions, titre = "Actions", declencheur, className }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; versLeHaut: boolean } | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);

  const visibles = actions.filter((a) => a.visible !== false);
  const ordinaires = visibles.filter((a) => !a.danger);
  const dangereuses = visibles.filter((a) => a.danger);

  const ouvrir = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (ouvert) { setOuvert(false); return; }
    const r = bouton.current?.getBoundingClientRect();
    if (!r) return;
    const hauteurEstimee = 40 * visibles.length + 24;
    const versLeHaut = r.bottom + hauteurEstimee > window.innerHeight && r.top > hauteurEstimee;
    setPosition({
      top: versLeHaut ? r.top - 6 : r.bottom + 6,
      left: Math.min(r.right, window.innerWidth - 8),
      versLeHaut,
    });
    setOuvert(true);
  };

  useEffect(() => {
    if (!ouvert) return;
    const fermerSiDehors = (e: MouseEvent) => {
      if (menu.current?.contains(e.target as Node) || bouton.current?.contains(e.target as Node)) return;
      setOuvert(false);
    };
    const fermerSurEchap = (e: KeyboardEvent) => { if (e.key === "Escape") setOuvert(false); };
    // Le bouton, à moitié caché dans un tableau qui défile, fait défiler la
    // page au clic : ce défilement-là ne doit pas refermer le menu.
    const ouvertA = Date.now();
    const fermer = () => { if (Date.now() - ouvertA > 400) setOuvert(false); };
    document.addEventListener("mousedown", fermerSiDehors);
    document.addEventListener("keydown", fermerSurEchap);
    window.addEventListener("scroll", fermer, true);
    window.addEventListener("resize", fermer);
    return () => {
      document.removeEventListener("mousedown", fermerSiDehors);
      document.removeEventListener("keydown", fermerSurEchap);
      window.removeEventListener("scroll", fermer, true);
      window.removeEventListener("resize", fermer);
    };
  }, [ouvert]);

  if (visibles.length === 0) return null;

  return (
    <>
      <button
        ref={bouton}
        type="button"
        onClick={ouvrir}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        title={titre}
        className={cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground transition-colors",
          "hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10",
          ouvert && "bg-black/5 text-foreground dark:bg-white/10",
          className
        )}
      >
        {declencheur ?? <MoreHorizontal className="h-4 w-4" />}
      </button>
      {ouvert && position && typeof document !== "undefined" && createPortal(
        <div
          ref={menu}
          role="menu"
          onClick={(e) => e.stopPropagation()}
          style={{ top: position.top, left: position.left, transform: `translate(-100%, ${position.versLeHaut ? "-100%" : "0"})` }}
          className="glass-dropdown fixed z-[70] min-w-[200px] rounded-xl border border-border/60 p-1.5 shadow-xl animate-in fade-in zoom-in-95 duration-100"
        >
          {ordinaires.map((a) => <Ligne key={a.libelle} action={a} fermer={() => setOuvert(false)} />)}
          {ordinaires.length > 0 && dangereuses.length > 0 && <div className="my-1.5 h-px bg-border/60" />}
          {dangereuses.map((a) => <Ligne key={a.libelle} action={a} fermer={() => setOuvert(false)} />)}
        </div>,
        document.body
      )}
    </>
  );
}

function Ligne({ action, fermer }: { action: ActionMenu; fermer: () => void }) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={() => { fermer(); action.onClick(); }}
      className={cn(
        "flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm transition-colors",
        action.danger
          ? "text-red-600 hover:bg-red-500/10 dark:text-red-400"
          : "text-foreground hover:bg-black/5 dark:hover:bg-white/10"
      )}
    >
      {action.icone && <span className="text-muted-foreground [&>svg]:h-4 [&>svg]:w-4">{action.icone}</span>}
      {action.libelle}
    </button>
  );
}
